/**
 * MainSection.tsx - Main Fundraiser Information Form Section
 *
 * This component handles the core fundraiser information form including
 * basic details, dates, goals, and categorization in the builder details step.
 *
 * Key Features:
 * - Fundraiser name and title with validation
 * - Subtitle and description text areas
 * - Start and end date selection with validation
 * - Fundraising goal with currency formatting
 * - Category selection with search and filtering
 * - Icon/image selection for fundraiser branding
 * - Real-time validation and error feedback
 *
 * Form Fields:
 * - Name: Internal fundraiser name (required)
 * - Title: Public display title (required)
 * - Subtitle: Optional tagline or brief description
 * - Description: Detailed fundraiser description
 * - Start Date: Campaign start date (required)
 * - End Date: Campaign end date (optional)
 * - Goal: Target fundraising amount (required)
 * - Category: Fundraiser category selection
 *
 * Validation Rules:
 * - Required field validation for name, title, dates, goal
 * - Date validation (start date before end date)
 * - Goal amount validation (minimum thresholds)
 * - Character limits for text fields
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, date utilities, form validation, category data
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { X, Plus, ChevronDown, Search, Check, Edit2 } from "lucide-react";
import { dateInputToDate, dateToDateInput } from "../../../utils/dateUtils";
import { EditMediaModal } from "../../media-editor/EditMediaModal";
import { IconType } from "../../../enums";
import {
  FlexContainer,
  ColorPicker as ColorPickerComponent,
} from "@mindmemobile/mm-shared-global";
import { SelectedColorButton } from "../../gallery-settings/GalleryAppearanceSection";
import { CategoryModel, CreateFundraiserModel } from "../../../models";
import { MediaItem } from "@mindmemobile/mm-shared-library";
import { MediaModel } from "../../../models/GalleryModels";
const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
  overflow: visible;
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns || 1}, 1fr)`};
  gap: 24px;
  margin-bottom: 24px;

  overflow: visible;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  overflow: visible;
  .checkout-color-picker {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-1px, 40px);
    z-index: 1001 !important;
    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }

      > div:first-child > div:first-child {
        height: 165px !important;
      }
    }
  }
`;

const RequiredLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;

  &::after {
    content: " *";
    color: #dc2626;
  }
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  padding: 16px;
  padding-right: 60px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  overflow-y: hidden;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const DescriptionContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EmojiContainer = styled.div`
  position: relative;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  gap: 4px;
`;

const EmojiOptions = styled.div<{ visible: boolean }>`
  display: flex;
  gap: 4px;
  margin-right: 8px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transform: translateX(${(props) => (props.visible ? "0" : "20px")});
  transition: all 0.3s ease;
  animation: ${(props) =>
    props.visible ? "slideInLeft 0.3s ease-out" : "none"};
  pointer-events: auto;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const EmojiOption = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.32);
  }
`;

const EmojiButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  filter: hue-rotate(15deg) saturate(1.2) brightness(1.1);
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.2);
  }
`;

const DateInput = styled.input`
  position: relative;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  /* When empty, show light gray */
  &:not([value]):not(:focus),
  &[value=""]:not(:focus) {
    color: #9ca3af;
  }

  /* When has value or focused, show black */
  &[value]:not([value=""]),
  &:focus {
    color: #1e293b;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ClearDateButton = styled.button<{ visible: boolean }>`
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  z-index: 5;

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.9);
  }
`;

const CurrencyInput = styled.div`
  position: relative;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
`;

const CurrencyField = styled.input`
  padding: 16px 16px 16px 40px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    color: #1e293b;
  }

  &:not(:placeholder-shown) {
    color: #1e293b;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconImageSection = styled.div`
  margin-bottom: 24px;
`;

const ChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ChoiceButtons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconImageRow = styled.div`
  display: grid;
  grid-template-columns: 300px 170px 44px;
  gap: 32px;
  align-items: end;
`;

const ButtonSection = styled.div``;

const IconSection = styled.div``;

const ColorSection = styled.div``;

const ChoiceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChoiceButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  height: 44px;
  border: 2px solid ${(props) => (props.active ? "#3b82f6" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.active ? "#f0f9ff" : "#ffffff")};
  color: ${(props) => (props.active ? "#3b82f6" : "#64748b")};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const IconSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-width: 150px;
  width: auto;
  max-width: 200px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorPicker = styled.input`
  width: 44px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const ColoredIconPreview = styled.div<{ bgColor: string; iconColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  color: ${(props) => props.iconColor};
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 240px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
  }
`;

const UploadIcon = styled.div`
  color: #94a3b8;
  margin-bottom: 8px;
  transition: color 0.2s ease;
`;

const UploadText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #64748b;
  transition: color 0.2s ease;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.2s ease;
`;

const FileInput = styled.input`
  display: none;
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LargeImagePreview = styled.img`
  max-width: 400px;
  min-height: 200px;
  max-height: 300px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
`;

const ImageDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const CategoryDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CategoryDropdownButton = styled.button<{
  isOpen: boolean;
  hasValue: boolean;
}>`
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: #ffffff;
  color: ${(props) => (props.hasValue ? "#1e293b" : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  color: #64748b;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const CategoryDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const CategorySearchContainer = styled.div`
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const CategorySearchIcon = styled(Search)`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #94a3b8;
`;

const CategorySearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const CategoryOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.isSelected ? "#f0f9ff" : "transparent")};
  color: #374151;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#f1f5f9")};
  }

  &:focus {
    outline: none;
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#e0f2fe")};
  }
`;

const SelectedCheckmark = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const ResetButton = styled.button`
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  z-index: 5;

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.9);
  }
`;

const EditIconButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: none;
  z-index: 10;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    transform: translateY(-50%) scale(1.05);
    box-shadow: none;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

interface MainSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  shouldShowError: (fieldName: string) => boolean;
  handleFieldBlur: (fieldName: string) => void;
  renderIcon: (iconName: string) => React.ReactNode;
  getIconColors: (color: string) => { bgColor: string; iconColor: string };
  setShowIconSelector: (show: boolean) => void;
  setIconSelectorImpactId: (id: string | null) => void;
  handleImageUpload: (files: FileList | null) => void;
  categories?: CategoryModel[];
  onKeyDown?: (e: React.KeyboardEvent, isLastField?: boolean) => void;
}

const additionalEmojis = [
  "❤️",
  "🙏",
  "😢",
  "💕",
  "👏",
  "🎉",
  "😇",
  "💪",
  "⭐",
  "🌎",
  "🐶",
  "🫶",
  "👍",
  "🐱",
  "🌱",
];

const categories = [
  "Advocacy & Causes",
  "Aging & Care",
  "Animals & Pets",
  "Celebrations & Events",
  "Community Projects",
  "Creative & Arts",
  "Education & Schools",
  "Emergency & Crisis Relief",
  "Environmental & Sustainability",
  "Food Instability",
  "Housing Assistance",
  "Human Rights & Equality",
  "International & Global Aid",
  "Medical & Health",
  "Memorial & Funeral",
  "Nonprofit & Charity",
  "Parenting & Fertility",
  "Religious & Faith-Based",
  "Sports & Teams",
  "Entrepreneurship & Startups",
  "Veterans & Military",
  "Youth & Kids",
].map((c) => new CategoryModel({ name: c }));

const CategoryDropdown: React.FC<{
  value: string;
  onChange: (value: CategoryModel) => void;
  onBlur: () => void;
}> = ({ value, onChange, onBlur }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategorySelect = (category: CategoryModel) => {
    onChange(category);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(new CategoryModel());
    setSearchTerm("");
  };

  return (
    <CategoryDropdownContainer ref={dropdownRef}>
      <CategoryDropdownButton
        type="button"
        isOpen={isOpen}
        hasValue={!!value}
        onClick={handleToggle}
      >
        <span>{value || "Select Category"}</span>
        {value && (
          <ResetButton
            onClick={handleReset}
            type="button"
            title="Clear category"
          >
            <X size={12} />
          </ResetButton>
        )}
        <ChevronIcon size={16} isOpen={isOpen} />
      </CategoryDropdownButton>

      <CategoryDropdownMenu isOpen={isOpen}>
        <CategorySearchContainer>
          <CategorySearchIcon />
          <CategorySearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </CategorySearchContainer>

        {filteredCategories.map((category, index) => (
          <CategoryOption
            key={index}
            isSelected={value === category.name}
            type="button"
            onClick={() => handleCategorySelect(category)}
          >
            <span>{category.name}</span>
            {value === category.name && (
              <SelectedCheckmark>
                <Check size={12} />
              </SelectedCheckmark>
            )}
          </CategoryOption>
        ))}

        {filteredCategories.length === 0 && (
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            No categories found
          </div>
        )}
      </CategoryDropdownMenu>
    </CategoryDropdownContainer>
  );
};

export const MainSection: React.FC<MainSectionProps> = observer(
  ({
    builderData,
    updateBuilderField,
    handleFieldBlur,
    renderIcon,
    getIconColors,
    setShowIconSelector,
    setIconSelectorImpactId,
    handleImageUpload,
    onKeyDown,
    categories,
  }) => {
    const [showAdditionalEmojis, setShowAdditionalEmojis] = useState(false);
    const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [showEditMediaModal, setShowEditMediaModal] = useState(false);
    const [editingMediaItem, setEditingMediaItem] = useState<MediaModel | null>(
      null
    );
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);

    const formatCurrency = (value: string) => {
      const numericValue = value.replace(/[^0-9.]/g, "");
      if (numericValue === "") return "";

      const number = parseFloat(numericValue);
      if (isNaN(number)) return "";

      return number.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    const hasStartDate =
      builderData.startDate && builderData.startDate !== null;
    const hasEndDate = builderData.endDate && builderData.endDate !== null;
    const iconImageChoice = builderData.iconImageChoice || IconType.None;

    const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
      textarea.style.height = "auto";
      textarea.style.height = Math.max(80, textarea.scrollHeight) + "px";
    };

    const handleDescriptionChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      updateBuilderField("description", e.target.value);
      autoResizeTextarea(e.target);
    };

    const handleEmojiClick = (emoji: string) => {
      if (descriptionTextareaRef.current) {
        const textarea = descriptionTextareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = builderData.description || "";
        const newDescription =
          currentValue.substring(0, start) +
          emoji +
          currentValue.substring(end);
        updateBuilderField("description", newDescription);

        // Set cursor position after the emoji
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd =
            start + emoji.length;
          textarea.focus();
          autoResizeTextarea(textarea);
        }, 0);
      } else {
        const currentValue = builderData.description || "";
        updateBuilderField("description", currentValue + emoji);
      }
    };

    const handlePrimaryEmojiClick = () => {
      if (!showAdditionalEmojis) {
        setShowAdditionalEmojis(true);
      } else {
        handleEmojiClick("😊");
      }
    };

    const handleEditLogoClick = () => {
      if (builderData.fundraiserImage) {
        const mediaItem = new MediaModel({
          ...builderData.fundraiserImage,
        });
        setEditingMediaItem(mediaItem);
        setShowEditMediaModal(true);
      }
    };

    const handleEditMediaSave = (updatedMedia: MediaModel) => {
      updateBuilderField(
        "fundraiserImage",
        new MediaModel({ ...updatedMedia })
      );
      setShowEditMediaModal(false);
      setEditingMediaItem(null);
    };

    const handleMainSectionImageClick = () => {
      updateBuilderField("showMainSectionLibrary", true);
    };

    // Auto-resize on mount and when description changes
    useEffect(() => {
      if (descriptionTextareaRef.current) {
        autoResizeTextarea(descriptionTextareaRef.current);
      }
    }, [builderData.description]);

    return (
      <Section className="main-section-wrapper">
        {/* Name field */}
        <FormGrid columns={2}>
          <FormGroup>
            <RequiredLabel htmlFor="name">Fundraiser Name</RequiredLabel>
            <Input
              id="name"
              type="text"
              placeholder="Enter fundraiser name"
              value={builderData.name}
              onChange={(e) => updateBuilderField("name", e.target.value)}
              onBlur={() => handleFieldBlur("name")}
              style={{
                borderColor: builderData.hasError("name")
                  ? "#dc2626"
                  : "#e2e8f0",
                backgroundColor: builderData.hasError("name")
                  ? "#fef2f2"
                  : "#ffffff",
              }}
            />
            {builderData.hasError("name") && (
              <ErrorMessage>{builderData.getErrorMessage("name")}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="category">Category</Label>
            <CategoryDropdown
              value={builderData.category.name || ""}
              onChange={(value: CategoryModel) =>
                updateBuilderField("category", value)
              }
              onBlur={() => handleFieldBlur("category")}
            />
          </FormGroup>
        </FormGrid>

        {/* Title and Subtitle fields */}
        <FormGrid columns={2}>
          <FormGroup>
            <RequiredLabel htmlFor="title">Title</RequiredLabel>
            <Input
              id="title"
              type="text"
              placeholder="Enter fundraiser title"
              value={builderData.title || ""}
              onChange={(e) => updateBuilderField("title", e.target.value)}
              onBlur={() => handleFieldBlur("title")}
              maxLength={40}
              style={{
                borderColor: builderData.hasError("title")
                  ? "#dc2626"
                  : "#e2e8f0",
                backgroundColor: builderData.hasError("title")
                  ? "#fef2f2"
                  : "#ffffff",
              }}
            />
            {builderData.hasError("title") && (
              <ErrorMessage>
                {builderData.getErrorMessage("title")}
              </ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="subtitle">Sub-Title</Label>
            <Input
              id="subtitle"
              type="text"
              placeholder="Enter fundraiser subtitle"
              value={builderData.subtitle || ""}
              onChange={(e) => updateBuilderField("subtitle", e.target.value)}
              onBlur={() => handleFieldBlur("subtitle")}
              maxLength={50}
            />
          </FormGroup>
        </FormGrid>

        {/* Icon/Image Section */}
        <IconImageSection>
          <ChoiceContainer>
            <IconImageRow>
              <ButtonSection>
                <Label>Icon / Logo</Label>
              </ButtonSection>
              {iconImageChoice === IconType.Icon && (
                <>
                  <IconSection>
                    <Label>Icon</Label>
                  </IconSection>
                  <ColorSection>
                    <Label>Color</Label>
                  </ColorSection>
                </>
              )}
            </IconImageRow>
            <IconImageRow>
              <ButtonSection>
                <ChoiceSection>
                  <ChoiceButtons>
                    <ChoiceButton
                      type="button"
                      active={iconImageChoice === IconType.None}
                      onClick={() =>
                        updateBuilderField("iconImageChoice", IconType.None)
                      }
                    >
                      None
                    </ChoiceButton>
                    <ChoiceButton
                      type="button"
                      active={iconImageChoice === IconType.Icon}
                      onClick={() =>
                        updateBuilderField("iconImageChoice", IconType.Icon)
                      }
                    >
                      Icon
                    </ChoiceButton>
                    <ChoiceButton
                      type="button"
                      active={iconImageChoice === IconType.Image}
                      onClick={() =>
                        updateBuilderField("iconImageChoice", IconType.Image)
                      }
                    >
                      Logo
                    </ChoiceButton>
                  </ChoiceButtons>
                </ChoiceSection>
              </ButtonSection>

              {iconImageChoice === IconType.Icon && (
                <>
                  <IconSection>
                    <IconSelectorButton
                      onClick={() => setShowIconSelector(true)}
                    >
                      <ColoredIconPreview
                        bgColor={
                          getIconColors(builderData.titleColor.hex).bgColor
                        }
                        iconColor={
                          getIconColors(builderData.titleColor.hex).iconColor
                        }
                      >
                        {renderIcon(builderData.titleIcon)}
                      </ColoredIconPreview>
                      {builderData.titleIcon}
                    </IconSelectorButton>
                  </IconSection>

                  {/* Custom Color Picker Section (replaces <ColorPicker type="color" />) */}
                  <ColorSection className="position-relative">
                    <FlexContainer
                      spacing={10}
                      alignItems="center"
                      className="position-relative"
                    >
                      {/* Color Button */}
                      <SelectedColorButton
                        heightWidth="24px"
                        borderRadius="2px"
                        onClick={() =>
                          setShowTitleColorPicker(!showTitleColorPicker)
                        }
                        color={
                          builderData.titleColor.rgba ||
                          builderData.titleColor.hex
                        }
                        className="color-picker-gallery "
                      >
                        <div />
                      </SelectedColorButton>

                      {/* Color Picker Popup */}
                      {showTitleColorPicker && (
                        <ColorPickerComponent
                          color={builderData.deserializeColor(
                            builderData.titleColor
                          )}
                          onChange={(color) =>
                            updateBuilderField("titleColor", color)
                          }
                          onHide={() => setShowTitleColorPicker(false)}
                          className="checkout-color-picker"
                        />
                      )}
                    </FlexContainer>
                  </ColorSection>
                </>
              )}
            </IconImageRow>

            {iconImageChoice === IconType.Image && (
              <div>
                {builderData.fundraiserImage.fullURL ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <ImageContainer>
                      <LargeImagePreview
                        src={builderData.fundraiserImage.fullURL}
                        alt="Fundraiser Logo"
                        style={{ cursor: "pointer" }}
                      />
                      <ImageDeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          updateBuilderField("fundraiserImage", null);
                        }}
                        type="button"
                      >
                        <X size={14} />
                      </ImageDeleteButton>
                    </ImageContainer>
                    <EditIconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditLogoClick();
                      }}
                      type="button"
                      title="Edit logo"
                      style={{
                        position: "static",
                        width: "32px",
                        height: "32px",
                        marginLeft: "15px",
                      }}
                    >
                      <Edit2 size={16} />
                    </EditIconButton>
                  </div>
                ) : (
                  <ImageUploadArea
                    onClick={() => handleMainSectionImageClick()}
                  >
                    <UploadIcon>
                      <Plus size={32} />
                    </UploadIcon>
                    <UploadText>Add Logo</UploadText>
                    <UploadSubtext>Select or upload an image</UploadSubtext>
                  </ImageUploadArea>
                )}

                <FileInput
                  id="fundraiser-image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />
              </div>
            )}
          </ChoiceContainer>
        </IconImageSection>

        {/* Start Date, End Date fields */}
        <FormGrid columns={2}>
          <FormGroup>
            <RequiredLabel htmlFor="startDate">Start Date</RequiredLabel>
            <DateInputContainer>
              <DateInput
                id="startDate"
                type="date"
                value={dateToDateInput(builderData.startDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    updateBuilderField(
                      "startDate",
                      dateInputToDate(e.target.value)
                    );
                  } else {
                    updateBuilderField("startDate", null);
                  }
                  handleFieldBlur("startDate");
                }}
                onBlur={() => handleFieldBlur("startDate")}
                onKeyDown={(e) => {
                  if (e.key === "Delete" || e.key === "Backspace") {
                    if (!e.currentTarget.value) {
                      e.preventDefault();
                      updateBuilderField("startDate", null);
                    }
                  }
                }}
                style={{
                  borderColor: builderData.hasError("startDate")
                    ? "#dc2626"
                    : "#e2e8f0",
                }}
              />
              <ClearDateButton
                visible={hasStartDate === true}
                onClick={() => updateBuilderField("startDate", null)}
                type="button"
                title="Clear start date"
              >
                <X size={12} />
              </ClearDateButton>
            </DateInputContainer>
            {builderData.hasError("startDate") && (
              <ErrorMessage>
                {builderData.getErrorMessage("startDate")}
              </ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="endDate">End Date</Label>
            <DateInputContainer>
              <DateInput
                id="endDate"
                type="date"
                value={dateToDateInput(builderData.endDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    updateBuilderField(
                      "endDate",
                      dateInputToDate(e.target.value)
                    );
                  } else {
                    updateBuilderField("endDate", null);
                  }
                }}
                onBlur={() => handleFieldBlur("endDate")}
                onKeyDown={(e) => {
                  if (e.key === "Delete" || e.key === "Backspace") {
                    if (!e.currentTarget.value) {
                      e.preventDefault();
                      updateBuilderField("endDate", null);
                    }
                  }
                }}
                style={{
                  borderColor: builderData.hasError("endDate")
                    ? "#dc2626"
                    : "#e2e8f0",
                }}
              />
              <ClearDateButton
                visible={hasEndDate === true}
                onClick={() => updateBuilderField("endDate", null)}
                type="button"
                title="Clear end date"
              >
                <X size={12} />
              </ClearDateButton>
            </DateInputContainer>
            {builderData.hasError("endDate") && (
              <ErrorMessage>
                {builderData.getErrorMessage("endDate")}
              </ErrorMessage>
            )}
          </FormGroup>
        </FormGrid>

        {/* Description field - full width below dates */}
        <FormGrid columns={1}>
          <DescriptionContainer>
            <DescriptionLabelRow>
              <Label htmlFor="description">Description</Label>
              <EmojiContainer>
                <EmojiOptions visible={showAdditionalEmojis}>
                  {additionalEmojis.map((emoji, index) => (
                    <EmojiOption
                      key={index}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                    >
                      {emoji}
                    </EmojiOption>
                  ))}
                </EmojiOptions>
                <EmojiButton type="button" onClick={handlePrimaryEmojiClick}>
                  😊
                </EmojiButton>
              </EmojiContainer>
            </DescriptionLabelRow>
            <TextArea
              ref={descriptionTextareaRef}
              id="description"
              placeholder="Enter fundraiser description"
              value={builderData.description || ""}
              onChange={handleDescriptionChange}
              onBlur={() => handleFieldBlur("description")}
              onKeyDown={(e) => {
                if (onKeyDown) {
                  onKeyDown(e, true);
                }
              }}
            />
          </DescriptionContainer>
        </FormGrid>

        {showEditMediaModal && editingMediaItem?.fullURL && (
          <EditMediaModal
            isVisible={showEditMediaModal}
            mediaItem={editingMediaItem}
            onClose={() => {
              setShowEditMediaModal(false);
              setEditingMediaItem(null);
            }}
            onSave={handleEditMediaSave}
            showDetailsPanel={false}
          />
        )}
      </Section>
    );
  }
);
